/* eslint eslint-comments/no-use: off */

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {IDS} from '@github-ui/item-picker-base/constants/ids'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {getAdjustedOverlayProps} from '@github-ui/item-picker-base/ItemPicker'
import type {ItemGroup} from '@github-ui/item-picker-base/shared'
import {SharedPicker} from '@github-ui/item-picker-base/SharedPicker'

import {GlobalCommands} from '@github-ui/ui-commands'
import {TableIcon} from '@primer/octicons-react'
import {PortalContext, SelectPanel, type SelectPanelProps, type SelectPanelItemInput as ItemInput} from '@primer/react'
import {FeatureFlags} from '@primer/react/experimental'
import {use, useCallback, useMemo, useRef, type HTMLAttributes, type RefAttributes} from 'react'

import {ProjectItemsLimitReachedDialog} from './ProjectItemsLimitReachedDialog'
import type {BaseItemPickerProps} from '@github-ui/item-picker-base/base-item-picker-props'

export type ProjectPickerBaseProject = {
  readonly id: string
  readonly title: string
  readonly closed: boolean
  readonly number: number
  readonly url: string
  readonly viewerCanUpdate: boolean
  readonly hasReachedItemsLimit: boolean
}

export type AnchoredOverlayAnchorProps = HTMLAttributes<HTMLElement> & RefAttributes<HTMLButtonElement>

export type ProjectPickerBaseProps = Omit<BaseItemPickerProps<ItemInput>, 'filterItems'> & {
  selectedProjects: ItemInput[]
  groups?: ItemGroup[]
}

type ProjectPickerInternalProps = {
  isDialogOpen: boolean
  handleDialogClose: () => void
} & Pick<SelectPanelProps, 'onFilterChange' | 'filterValue' | 'onOpenChange' | 'onCancel'>

const DEFAULT_FILTERED_PROJECTS: ProjectPickerBaseProject[] = []
const DEFAULT_GROUPS: ItemGroup[] = []
export function ProjectPickerBase({
  items: filteredProjects = DEFAULT_FILTERED_PROJECTS,
  groups = DEFAULT_GROUPS,
  selectedProjects,
  isDialogOpen,
  handleDialogClose,
  filterValue,
  onCancel,
  onOpenChange,
  onFilterChange,
  anchorElement,
  onSelectionChange: onSelectedChange,
  insidePortal,
  isLoading: loading,
  title,
  triggerOpen: open,
  anchorSide,
}: ProjectPickerBaseProps & ProjectPickerInternalProps) {
  const {portalContainerName} = use(PortalContext)
  const handleGlobalCommand = useCallback(() => {
    if (open) return

    // it's not really an anchor press, but it's a key command, so close enough?
    onOpenChange(true, 'anchor-key-press')
  }, [open, onOpenChange])

  const buttonRef = useRef<HTMLButtonElement>(null)
  const regularHeight = filteredProjects.length <= 2 ? 'auto' : 'large'
  const responsiveOnNarrowEnabled = isFeatureEnabled('issues_react_select_panel_fullscreen_on_narrow')

  const adjustedOverlayProps = useMemo(
    () =>
      getAdjustedOverlayProps(
        insidePortal && !portalContainerName,
        buttonRef, // eslint-disable-line react-hooks/refs
        regularHeight,
        responsiveOnNarrowEnabled,
        open,
      ),
    [insidePortal, regularHeight, responsiveOnNarrowEnabled, open, portalContainerName],
  )

  const showNoMatchMessage = useMemo(() => filteredProjects.length === 0, [filteredProjects.length])
  const selectPanelProps = useMemo<SelectPanelProps>(() => {
    const props: SelectPanelProps = {
      renderAnchor: anchorProps => anchorElement(anchorProps),
      anchorRef: buttonRef,
      placeholderText: LABELS.filterProjects,
      cssAnchorPositioningSettings: {fallbackStrategy: 'opposite-side'},
      items: filteredProjects,
      selected: selectedProjects,
      open: open || false,
      onOpenChange,
      onCancel,
      onSelectedChange,
      filterValue,
      onFilterChange,
      showItemDividers: true,
      overlayProps: {
        width: 'medium',
        ...adjustedOverlayProps,
        anchorSide,
        portalContainerName,
      } as const,
      loading,
      title: title ?? LABELS.selectProjects,
      message: showNoMatchMessage
        ? {
            title: 'No projects were found',
            variant: 'empty',
            body: 'Please try a different search query.',
          }
        : undefined,
    }

    if (groups?.length > 0 && filteredProjects.length > 0) {
      // eslint-disable-next-line react-hooks/refs
      props.groupMetadata = groups
    }

    return props
  }, [
    adjustedOverlayProps,
    anchorElement,
    anchorSide,
    filterValue,
    filteredProjects,
    groups,
    loading,
    onCancel,
    onFilterChange,
    onOpenChange,
    onSelectedChange,
    open,
    selectedProjects,
    portalContainerName,
    showNoMatchMessage,
    title,
  ])

  return (
    <>
      <GlobalCommands commands={{'item-pickers:open-projects': handleGlobalCommand}} />
      <FeatureFlags flags={{primer_react_select_panel_fullscreen_on_narrow: responsiveOnNarrowEnabled}}>
        <SelectPanel
          aria-label="Project results"
          data-id={IDS.itemPickerRootId}
          data-testid={IDS.itemPickerTestId}
          {...selectPanelProps}
        />
      </FeatureFlags>
      {isDialogOpen && <ProjectItemsLimitReachedDialog onClose={handleDialogClose} returnFocusRef={buttonRef} />}
    </>
  )
}

export type DefaultProjectPickerAnchorProps = Partial<Pick<ProjectPickerBaseProps, 'readonly' | 'nested'>> & {
  firstSelectedProjectTitle?: string
  anchorProps?: AnchoredOverlayAnchorProps | undefined
}

export function DefaultProjectPickerAnchor({
  firstSelectedProjectTitle,
  readonly,
  nested,
  anchorProps,
}: DefaultProjectPickerAnchorProps) {
  return (
    <SharedPicker
      anchorText={LABELS.noProjects}
      sharedPickerMainValue={firstSelectedProjectTitle}
      anchorProps={readonly ? undefined : anchorProps}
      ariaLabel={LABELS.selectProjects}
      readonly={readonly}
      nested={nested}
      leadingIcon={TableIcon}
      hotKey={undefined}
    />
  )
}
